import { useCallback, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PillTabs } from "@/components/ui/pill-tabs";
import { FilterBar, FilterSearch } from "@/components/ui/filter-bar";
import { ReportTable } from "@/components/reports/ReportTable";
import { ReportDetailDialog } from "@/components/reports/ReportDetailDialog";
import { useReports } from "@/hooks/useReports";
import type { ReportStatus } from "@/types";

type StatusFilter = ReportStatus | "all";

const STATUS_LABELS: Record<StatusFilter, string> = {
  open: "Otwarte",
  resolved: "Rozwiązane",
  all: "Wszystkie",
};

export default function ReportsPage() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("open");
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(25);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);

  const handleStatusChange = useCallback((v: string) => {
    setStatusFilter(v as StatusFilter);
    setCursorStack([]);
  }, []);

  const handleSearchChange = useCallback((v: string) => {
    setSearch(v);
    setCursorStack([]);
  }, []);

  const handleLimitChange = (v: string) => {
    setLimit(Number(v));
    setCursorStack([]);
  };

  const cursor = cursorStack.at(-1);
  const {
    data: result,
    isLoading,
    error,
  } = useReports({
    status: statusFilter === "all" ? undefined : statusFilter,
    search: search || undefined,
    cursor,
    limit,
  });

  const reports = result?.data ?? [];
  const hasMore = result?.hasMore ?? false;
  const nextCursor = result?.nextCursor ?? null;

  const goNext = () => {
    if (nextCursor) setCursorStack((prev) => [...prev, nextCursor]);
  };
  const goPrev = () => setCursorStack((prev) => prev.slice(0, -1));
  const page = cursorStack.length + 1;

  return (
    <div>
      <PageHeader
        title="Zgłoszenia"
        description="Zgłoszenia błędów w wydarzeniach od użytkowników aplikacji."
      />

      <FilterBar>
        <PillTabs
          value={statusFilter}
          onChange={handleStatusChange}
          options={(["open", "resolved", "all"] as StatusFilter[]).map((s) => ({
            value: s,
            label: STATUS_LABELS[s],
          }))}
        />
        <FilterSearch
          value={search}
          onChange={handleSearchChange}
          placeholder="Szukaj w opisie..."
        />
      </FilterBar>

      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-12 rounded bg-slate-100 animate-pulse" />
          ))}
        </div>
      )}
      {error && <p className="text-destructive">Błąd ładowania zgłoszeń: {error.message}</p>}
      {!isLoading && !error && <ReportTable reports={reports} onOpen={(id) => setOpenId(id)} />}

      {!isLoading && (reports.length > 0 || cursorStack.length > 0) && (
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Strona {page}</span>
            <Select value={String(limit)} onValueChange={handleLimitChange}>
              <SelectTrigger className="w-24 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
            <span>na stronę</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={goPrev}
              disabled={cursorStack.length === 0}
            >
              <ChevronLeft className="h-4 w-4" />
              Poprzednia
            </Button>
            <Button variant="outline" size="sm" onClick={goNext} disabled={!hasMore}>
              Następna
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <ReportDetailDialog reportId={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}
